import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  LabelList,
} from "recharts";
import PlatformIcon from "../../../../common/components/Icon/PlatformIcon/PlatformIcon";

// Minimal session shape needed for the aggregation, so the chart stays
// decoupled from the full ISession model.
export interface DeviceSession {
  target_platform?: string | null;
  device_model?: string | null;
}

interface SessionsByDeviceChartProps {
  sessions: DeviceSession[];
}

// Cap the model breakdown in the tooltip: an unbounded list can grow taller than
// the chart itself and stretch the page (the chart sits at the bottom of the view)
const MAX_MODELS_IN_TOOLTIP = 4;

// Wide enough for the platform icon + label (the MetaQuest wordmark icon alone is 90px)
const Y_AXIS_WIDTH = 170;

// Same green gradient as TopParcoursChart, to stay consistent with the other stats charts
const COLORS = [
  "#00c889",
  "#00b47b",
  "#009667",
  "#007852",
  "#006243",
  "#004d35",
  "#003d2a",
  "#002d1f",
];

interface DeviceChartDatum {
  platform: string;
  name: string;
  sessions: number;
  // "158 (99%)" — rendered at the end of the bar so values are readable without hovering
  label: string;
  models: Array<[string, number]>;
}

const SessionsByDeviceChart = ({ sessions }: SessionsByDeviceChartProps) => {
  const { t } = useTranslation();

  const chartData = useMemo((): DeviceChartDatum[] => {
    const byPlatform = new Map<string, { count: number; models: Map<string, number> }>();

    sessions.forEach((session) => {
      const platform = session.target_platform || "Unknown";
      const entry = byPlatform.get(platform) || { count: 0, models: new Map<string, number>() };
      entry.count++;
      if (session.device_model) {
        entry.models.set(session.device_model, (entry.models.get(session.device_model) || 0) + 1);
      }
      byPlatform.set(platform, entry);
    });

    const total = sessions.length;

    return Array.from(byPlatform.entries())
      .map(([platform, entry]) => ({
        platform,
        name: t(`statsNew.session.platform.${platform}`, { defaultValue: platform }),
        sessions: entry.count,
        label: `${entry.count} (${Math.round((entry.count / total) * 100)}%)`,
        models: Array.from(entry.models.entries()).sort((a, b) => b[1] - a[1]),
      }))
      .sort((a, b) => b.sessions - a.sessions);
  }, [sessions, t]);

  // Tooltip mirrors the card style of the other stats charts, with the
  // device model breakdown when sessions reported one.
  const DeviceTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: Array<{ payload: DeviceChartDatum }>;
  }) => {
    if (!active || !payload?.length) return null;
    const datum = payload[0].payload;
    const shownModels = datum.models.slice(0, MAX_MODELS_IN_TOOLTIP);
    const hiddenCount = datum.models.length - shownModels.length;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-md px-3 py-2">
        <p className="text-sm font-medium text-gray-900">{datum.name}</p>
        <p className="text-sm text-gray-600">
          {datum.sessions} {t("statsNew.charts.sessions")}
        </p>
        {shownModels.map(([model, count]) => (
          <p key={model} className="text-xs text-gray-500">
            {model} : {count}
          </p>
        ))}
        {hiddenCount > 0 && (
          <p className="text-xs text-gray-400">
            {t("statsNew.charts.moreModels", { count: hiddenCount })}
          </p>
        )}
      </div>
    );
  };

  // Custom Y-axis tick: same platform icons as on the session cards, next to the label.
  // Icons are plain <svg> components, so they can be nested inside the chart SVG.
  const PlatformTick = ({
    x,
    y,
    payload,
  }: {
    x?: number;
    y?: number;
    payload?: { value: string };
  }) => {
    const datum = chartData.find((d) => d.name === payload?.value);
    const tickX = x ?? 0;
    const tickY = y ?? 0;
    // PlatformIcon renders every icon at the requested 20px, except the MetaQuest
    // wordmark which is hardcoded to 90px wide — reserve the real footprint so the
    // label sits flush against the icon.
    const isMetaQuest = datum?.platform === "MetaQuest" || datum?.platform === "OculusQuest";
    const iconWidth = isMetaQuest ? 90 : 20;
    return (
      <g>
        {datum && (
          <g transform={`translate(${tickX - iconWidth - 2}, ${tickY - 10})`}>
            <PlatformIcon
              platform={datum.platform}
              iconProps={{ width: "20px", height: "20px", fill: "#6b7280" }}
            />
          </g>
        )}
        <text
          x={tickX - (datum ? iconWidth + 10 : 0)}
          y={tickY}
          dy={4}
          textAnchor="end"
          fontSize={12}
          fill="#374151"
        >
          {payload?.value ?? ""}
        </text>
      </g>
    );
  };

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-500">
        {t("statsNew.charts.noData")}
      </div>
    );
  }

  // Enough room for every platform row without squashing the bars
  const chartHeight = Math.max(140, chartData.length * 48 + 40);

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <BarChart
        data={chartData}
        layout="vertical"
        margin={{ top: 5, right: 70, left: 0, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#e5e7eb" />
        <XAxis
          type="number"
          tick={{ fontSize: 11, fill: "#6b7280" }}
          tickLine={false}
          axisLine={{ stroke: "#e5e7eb" }}
          allowDecimals={false}
        />
        <YAxis
          type="category"
          dataKey="name"
          tick={<PlatformTick />}
          tickLine={false}
          axisLine={{ stroke: "#e5e7eb" }}
          width={Y_AXIS_WIDTH}
          interval={0}
        />
        {/* y pinned to the top: the chart sits at the bottom of the page, so a
            cursor-following tooltip would overflow below and stretch the page */}
        <Tooltip
          content={<DeviceTooltip />}
          cursor={{ fill: "rgba(0, 200, 137, 0.1)" }}
          position={{ y: 0 }}
        />
        <Bar dataKey="sessions" radius={[0, 4, 4, 0]} maxBarSize={30}>
          {chartData.map((_, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
          <LabelList dataKey="label" position="right" style={{ fontSize: 11, fill: "#374151" }} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export default SessionsByDeviceChart;
